import JSZip from 'jszip';
import { saveAs } from 'file-saver';

/**
 * Parse AI response and extract files for project generation
 */
export const parseProjectFiles = (content) => {
  const files = [];
  
  // Pattern to match file definitions with path
  // Matches: ```language:path/to/file.ext or // filename: path/to/file.ext followed by code
  const filePatterns = [
    // Pattern 1: ```language:filepath
    /```(\w+)?:([^\n]+)\n([\s\S]*?)```/g,
    // Pattern 2: Standard code blocks with filename comment
    /(?:\/\/|#|<!--)\s*(?:file|filename|archivo):\s*([^\n]+)\n```(\w+)?\n?([\s\S]*?)```/gi,
  ];

  // Try pattern 1
  let match;
  const pattern1 = /```(\w+)?:([^\n]+)\n([\s\S]*?)```/g;
  while ((match = pattern1.exec(content)) !== null) {
    files.push({
      path: match[2].trim(),
      content: match[3].trim(),
      language: match[1] || 'txt'
    });
  }

  // If no files found with pattern 1, try to extract from standard code blocks
  if (files.length === 0) {
    const codeBlockRegex = /```(\w+)?\n?([\s\S]*?)```/g;
    let blockIndex = 0;
    
    while ((match = codeBlockRegex.exec(content)) !== null) {
      const language = match[1] || 'txt';
      const code = match[2].trim();
      
      if (code) {
        // Try to detect filename from first line comment
        const firstLineMatch = code.match(/^(?:\/\/|#|<!--)\s*([^\n]+)/);
        let filename;
        let cleanCode = code;
        
        if (firstLineMatch && firstLineMatch[1].includes('.')) {
          filename = firstLineMatch[1].trim();
          cleanCode = code.replace(/^(?:\/\/|#|<!--)[^\n]+\n?/, '').trim();
        } else {
          // Generate filename based on language
          const ext = getExtension(language);
          filename = `file_${blockIndex + 1}.${ext}`;
        }
        
        files.push({
          path: filename,
          content: cleanCode,
          language
        });
        blockIndex++;
      }
    }
  }

  return files;
};

const getExtension = (lang) => {
  const extensions = {
    javascript: 'js',
    typescript: 'ts',
    python: 'py',
    html: 'html',
    css: 'css',
    json: 'json',
    jsx: 'jsx',
    tsx: 'tsx',
    sql: 'sql',
    bash: 'sh',
    shell: 'sh',
    yaml: 'yaml',
    yml: 'yml',
    markdown: 'md',
    java: 'java',
    cpp: 'cpp',
    c: 'c',
    go: 'go',
    rust: 'rs',
    php: 'php',
    ruby: 'rb',
  };
  return extensions[lang?.toLowerCase()] || 'txt';
};

/**
 * Download multiple files as a ZIP archive
 */
export const downloadProjectAsZip = async (files, projectName = 'proyecto') => {
  const zip = new JSZip();

  files.forEach(file => {
    // Handle nested paths by creating folders
    zip.file(file.path, file.content);
  });

  const blob = await zip.generateAsync({ type: 'blob' });
  saveAs(blob, `${projectName}.zip`);
};

/**
 * Download conversation code as a project
 */
export const downloadConversationAsProject = async (messages, projectName = 'proyecto') => {
  const allFiles = [];
  
  // Extract files from all assistant messages
  messages.forEach(msg => {
    if (msg.role === 'assistant') {
      const files = parseProjectFiles(msg.content);
      allFiles.push(...files);
    }
  });

  if (allFiles.length === 0) {
    throw new Error('No se encontró código para descargar en esta conversación');
  }

  await downloadProjectAsZip(allFiles, projectName);
  return allFiles.length;
};

/**
 * 🟥 Helper público — extractor de archivos del proyecto activo.
 * Lo usan Dashboard.jsx (para el CodePanel) y SaveToGitHubButton.
 *
 * 🟪 REVERT 2026-02 (feedback owner): SIN deduplicación. Muestra TODAS
 * las versiones históricas de cada archivo en el sidebar
 * (Calculator.jsx, Calculator-2.jsx, …, Calculator-12.jsx). El owner
 * quiere ver la evolución completa, no solo la última versión.
 */
export const extractConversationFiles = (messages) => {
  const allFiles = [];
  (messages || []).forEach((msg) => {
    if (msg && msg.role === 'assistant') {
      const files = parseProjectFiles(msg.content);
      allFiles.push(...files);
    }
  });
  return allFiles;
};

